import { useState } from 'react';
import { ArrowLeft, ChevronDown, ChevronUp, Search, Heart, Shield, MessageCircle, Map, Calendar, User, Bell, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';

const FAQItem = ({ question, answer, icon: Icon }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-[#FF2E63] flex-shrink-0" />}
          <span className="text-white font-medium">{question}</span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-[#A1A1AA] flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-[#A1A1AA] flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 text-[#A1A1AA] leading-relaxed">
          <div className="pl-8">{answer}</div>
        </div>
      )}
    </div>
  );
};

const FAQ = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const faqCategories = [
    {
      title: "Getting Started",
      icon: User,
      questions: [
        {
          question: "What is PULSE?",
          answer: "PULSE is a location-based social app designed for the LGBTQ+ community. Connect with people nearby, chat, discover events, and find your community.",
          icon: Heart
        },
        {
          question: "How do I create a profile?",
          answer: "After signing up with your email, go to your Profile tab. Add photos, write a bio, and fill in your details. The more complete your profile, the better your matches!",
          icon: User
        },
        {
          question: "Is the beta free?",
          answer: "Yes! During beta testing, ALL features are unlocked for everyone. Enjoy premium features like advanced filters, incognito mode, and unlimited taps at no cost.",
          icon: Heart
        }
      ]
    },
    {
      title: "Finding & Connecting",
      icon: Heart,
      questions: [
        {
          question: "How does Browse work?",
          answer: "Browse shows users near you, sorted by distance and online status. Tap on a profile to view details, send a tap (like), or start a conversation.",
          icon: Map
        },
        {
          question: "What's a 'Tap'?",
          answer: "A tap is like a poke or like - it shows someone you're interested. If they tap you back, it's a match! You can also message directly without tapping first.",
          icon: Heart
        },
        {
          question: "How accurate is the distance?",
          answer: "We show approximate distance (e.g., '2 km away') to protect privacy. We never share your exact location with other users.",
          icon: Map
        }
      ]
    },
    {
      title: "Privacy & Safety",
      icon: Shield,
      questions: [
        {
          question: "Who can see my profile?",
          answer: "Only logged-in users can see profiles. You can hide anytime using the visibility toggle in Settings. Use Incognito mode to browse without appearing in 'Viewed' lists.",
          icon: Shield
        },
        {
          question: "How do I block someone?",
          answer: "Open their profile, tap the menu (three dots), and select 'Block'. They won't be able to see your profile, message you, or know they've been blocked.",
          icon: Shield
        },
        {
          question: "How do I report inappropriate behavior?",
          answer: "Tap the menu on any profile or message and select 'Report'. Choose the reason and our team will review it. Reports are confidential.",
          icon: Shield
        },
        {
          question: "Are my messages private?",
          answer: "Yes, messages are between you and the recipient. We don't read your messages unless investigating a report of abuse or illegal activity.",
          icon: MessageCircle
        },
        {
          question: "What's Incognito mode?",
          answer: "Incognito lets you browse profiles without showing up in their 'Viewed Me' list. Perfect for discreet browsing.",
          icon: Shield
        }
      ]
    },
    {
      title: "Messaging & Chat",
      icon: MessageCircle,
      questions: [
        {
          question: "How do I start a conversation?",
          answer: "Open someone's profile and tap the message icon. No match required - you can message anyone! Start with something from their profile for better responses.",
          icon: MessageCircle
        },
        {
          question: "What are expiring photos?",
          answer: "Expiring photos disappear after being viewed once. Great for sharing moments without them being saved. The recipient sees a countdown timer.",
          icon: MessageCircle
        },
        {
          question: "Can I unsend a message?",
          answer: "Yes! Long-press or hover on your sent message and tap 'Unsend'. The message will be removed from both sides of the conversation.",
          icon: MessageCircle
        },
        {
          question: "What do the checkmarks mean?",
          answer: "One check = sent. Two checks = delivered. Blue checks = read. You can see when someone is typing in real-time.",
          icon: MessageCircle
        }
      ]
    },
    {
      title: "Events & Meetups",
      icon: Calendar,
      questions: [
        {
          question: "How do I find events?",
          answer: "Go to the Events tab to browse upcoming LGBTQ+ events in your area. Filter by type (Pride, Party, Networking) and register for ones you're interested in.",
          icon: Calendar
        },
        {
          question: "Can I create my own meetup?",
          answer: "Yes! On the Map view, tap '+ Meetup' to create a casual gathering. Set a location, time, and description. Others can see and join your meetup.",
          icon: Calendar
        },
        {
          question: "What are hotspots?",
          answer: "Hotspots are popular locations discovered by the community - bars, clubs, cafes, etc. They help you find LGBTQ+-friendly venues in your area.",
          icon: Map
        }
      ]
    },
    {
      title: "Notifications",
      icon: Bell,
      questions: [
        {
          question: "How do I enable notifications?",
          answer: "Go to Profile > Settings and look for 'Push Notifications'. Tap 'Enable Notifications' and allow when prompted. You can customize which notifications you receive.",
          icon: Bell
        },
        {
          question: "What notifications will I get?",
          answer: "You can receive alerts for: new messages, taps & matches, profile views, and event reminders. Toggle each type on/off in Settings.",
          icon: Bell
        },
        {
          question: "I'm not getting notifications?",
          answer: "Check that notifications are enabled in both the app (Profile > Settings) and your phone's settings. Make sure PULSE has permission to send notifications.",
          icon: Bell
        }
      ]
    },
    {
      title: "Beta Testing",
      icon: HelpCircle,
      questions: [
        {
          question: "What does 'beta' mean?",
          answer: "Beta means we're still testing and improving the app before the official launch. You get early access and all features free! You might encounter occasional bugs.",
          icon: HelpCircle
        },
        {
          question: "How can I give feedback?",
          answer: "We love feedback! Use the feedback feature in the app, or report bugs when you see them. Your input directly shapes the final product.",
          icon: HelpCircle
        },
        {
          question: "Will my account carry over after beta?",
          answer: "Yes! Your profile, connections, and messages will remain after beta ends. Some data may be reset if necessary during testing, but we'll notify you.",
          icon: HelpCircle
        },
        {
          question: "Are premium features free during beta?",
          answer: "Yes! All beta testers get full access to every feature including Incognito mode, advanced filters, expiring photos, and unlimited taps.",
          icon: Heart
        }
      ]
    }
  ];

  // Flatten all questions for search
  const allQuestions = faqCategories.flatMap(cat => 
    cat.questions.map(q => ({ ...q, category: cat.title }))
  );

  // Filter by search
  const filteredCategories = searchQuery
    ? [{
        title: "Search Results",
        icon: Search,
        questions: allQuestions.filter(q => 
          q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          q.answer.toLowerCase().includes(searchQuery.toLowerCase())
        )
      }]
    : faqCategories;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#A1A1AA] hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>

        <h1 className="text-3xl font-bold text-[#FF2E63] mb-2">Help & FAQ</h1>
        <p className="text-[#A1A1AA] mb-6">Find answers to common questions</p>

        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search for help..."
            className="pl-12 bg-white/5 border-white/10 h-12 text-white placeholder:text-[#52525B]"
          />
        </div>

        {/* FAQ Categories */}
        <div className="space-y-8">
          {filteredCategories.map((category, idx) => (
            <div key={idx}>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <category.icon className="w-5 h-5 text-[#08D9D6]" />
                {category.title}
              </h2>
              <div className="space-y-3">
                {category.questions.length > 0 ? (
                  category.questions.map((faq, faqIdx) => (
                    <FAQItem key={faqIdx} {...faq} />
                  ))
                ) : (
                  <p className="text-[#52525B] text-center py-8">No results found. Try a different search term.</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Still need help */}
        <div className="mt-12 p-6 bg-gradient-to-r from-[#FF2E63]/10 to-[#7000FF]/10 border border-[#FF2E63]/30 rounded-xl text-center">
          <h3 className="text-lg font-semibold text-white mb-2">Still have questions?</h3>
          <p className="text-[#A1A1AA] mb-4">
            We're here to help! Use the feedback feature in the app to reach our support team.
          </p>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 text-center text-[#52525B] text-sm">
          <p>PULSE - Connect with your community</p>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
